import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { AdminScaleId, AdminScaleChildResponse, AdminScaleChildId, TreeNode, AdminScaleChildType } from '@business/entities/adminscale';
import { AdminScaleChildAPI } from '@business/api/admin_scale_childs';
import { showDeleteConfirmation } from '@modules/common';
import { switchMap } from 'rxjs/operators';

export class AdminScaleChildBloc extends BaseBloc {
  private readonly adminScaleChildRepo = new Repository<TreeNode | AdminScaleChildResponse>({
    getItemId: item => item.id,
  });

  readonly adminScaleChilds = this.adminScaleChildRepo.items;
  readonly allAdminScaleChilds$ = this.adminScaleChildRepo.items$;
  readonly selectAdminScaleChild = this.adminScaleChildRepo.selectItem;

  onReset() {
    this.adminScaleChildRepo.reset();
  }

  fetchAdminScaleChilds = (adminScaleId: AdminScaleId, fullResponse?: boolean) => {
    return AdminScaleChildAPI.fetchAdminScaleChilds(adminScaleId, fullResponse).pipe(
      this.adminScaleChildRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_adminscale_error') }),
    );
  };

  fetchAdminScaleChild = (id: AdminScaleChildId) => {
    return AdminScaleChildAPI.fetchAdminScaleChild(id).pipe(
      this.adminScaleChildRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_adminscale_error') }),
    );
  };

  searchPolicy = (search: string) => {
    return AdminScaleChildAPI.searchPolicy(search).pipe(handleMessage({ error: requestMessage('fetch_adminscale_policy_error') }));
  };

  searchStatistics = (search: string) => {
    return AdminScaleChildAPI.searchStatistics(search).pipe(handleMessage({ error: requestMessage('fetch_adminscale_statistics_error') }));
  };

  createAdminScaleChild = (data: Partial<AdminScaleChildResponse>) => {
    return AdminScaleChildAPI.createAdminScaleChild(data).pipe(
      this.adminScaleChildRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('create_adminscale_child'),
      }),
    );
  };

  updateAdminScaleChild = (id: AdminScaleId, data: Partial<AdminScaleChildResponse>) => {
    return AdminScaleChildAPI.updateAdminScaleChild(id, data).pipe(
      this.adminScaleChildRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_adminscale_child'),
      }),
    );
  };

  deleteAdminScaleChild = (id: AdminScaleId) => {
    return showDeleteConfirmation('Delete Admin Scale Child', 'Do you really want to remove this Admin Scale Child?').pipe(
      switchMap(() => AdminScaleChildAPI.deleteAdminScaleChild(id)),
      this.adminScaleChildRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_adminscale_child'),
      }),
    );
  };

  sortAdminScale = (
    adminScaleChild: AdminScaleChildResponse,
    adminScaleId: AdminScaleId,
    toColumnId: number,
    toPosition: number,
    fromColumnId: number,
  ) => {
    return AdminScaleChildAPI.sortAdminScale(adminScaleChild, adminScaleId, toColumnId, toPosition, fromColumnId).pipe(
      this.adminScaleChildRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_adminscale_sorted'),
      }),
    );
  };
}
